import React, {FC, useCallback, useRef, useState, useEffect} from "react";
import {
  Container,
  TitleBig,
  NavWrapper,
  AlertsWrapper,
} from './styles'
import { useTranslation } from "react-i18next";
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { BackArrow, NotificationBlock, Tabs, TopProgress } from "../../../components";
import { objStorageService } from "../../../api";
import {
  FilesPage,
  SettingsPage,
  LifecycleRulesPage,
} from './components'
import { tabPermissionSurfaces, usePermission, usePermissionDecision } from '../../../permissions';
import { useAuth } from "../../../context/user.context";

const BucketPage:FC = () => {
  const [t] = useTranslation()
  const canViewFiles = usePermissionDecision(tabPermissionSurfaces.bucketFiles).allowed
  const canViewSettings = usePermissionDecision(tabPermissionSurfaces.bucketSettings).allowed
  const canViewLifecycleRules = usePermissionDecision(tabPermissionSurfaces.bucketLifecycleRules).allowed
  const tabs = [
    {
      title:t("FILES"),
      slug:'files',
      id:'1',
      visible: canViewFiles,
    },
    {
      title:t("BUCKET_SETTINGS"),
      slug:'settings',
      id:'2',
      visible: canViewSettings,
    },
    {
      title:t("LIFECYCLE_RULES"),
      slug:'lifecycle-rules',
      id:'3',
      visible: canViewLifecycleRules,
    },
  ]
  const visibleTabs = tabs.filter((tab) => tab.visible)
  const defaultTab = visibleTabs[0]?.slug
  const [activeTab, setActiveTab] = useState<string>()
  const [bucket, setBucket] = useState<any>()
  const [pageLoading, setPageLoading] = useState<boolean>(true)
  const navigate = useNavigate()
  const {bucketId} = useParams()
  const { mergeEntityPermissions } = usePermission()
  const { user } = useAuth()
  const activeClientId = user?.user?.active_client
  const requestScope = useRef(0)
  const getData = useCallback(async (includeChildPermissions = false) => {
    const scope = requestScope.current
    // Fetch bucket data here
    try{
      //@ts-ignore
      const resBucket = await objStorageService.getBucketById(bucketId)

      if (scope !== requestScope.current) {
        return
      }

      // setFiles(resBucket.data)
      mergeEntityPermissions('objectStorage', resBucket.data, 'Merged bucket detail permissions')

      if (includeChildPermissions) {
        const permissionResponses = await Promise.allSettled([
          objStorageService.getBucketFilePermissions(bucketId as string),
          objStorageService.getBucketPathPermissions(bucketId as string),
        ])

        if (scope !== requestScope.current) {
          return
        }

        permissionResponses.forEach((response) => {
          if (response.status === 'fulfilled') {
            mergeEntityPermissions('objectStorage', response.value.data, 'Merged bucket child permissions')
          }
        })
      }

      if (scope !== requestScope.current) {
        return
      }

      setBucket(resBucket.data)
      setPageLoading(false)
    } catch (err:any) {
      if (scope !== requestScope.current) {
        return
      }

      setPageLoading(false)
      console.log('err',err);
    }
  }, [bucketId, mergeEntityPermissions])
  useEffect(() => {
    requestScope.current += 1
    setBucket(undefined)
    setPageLoading(true)
    void getData(true)
    const interval = setInterval(() => {
      void getData()
    }, 5000)
    return () => {
      requestScope.current += 1
      clearInterval(interval)
    }
  },[activeClientId, getData])
  const location = useLocation()
  useEffect(() => {
    visibleTabs.forEach((item:any) => {
      if(location.pathname.includes(item.slug)){
        setActiveTab(item.slug)
      }
    })
  },[location, visibleTabs])
  if(pageLoading) return <TopProgress loading={pageLoading}/>
  if (!defaultTab) return <Navigate replace to="/403" />
  return(
    <Container>
      <BackArrow
        customStyles={{marginLeft:'30px'}}
        title={t("BACK_TO_OBJECT_STORAGE_LIST")}
        onClick={() => navigate('/object-storage/buckets')}
      />
      <TitleBig>{bucket.name}</TitleBig>
      <AlertsWrapper>
        <NotificationBlock 
          severity="info"
          title={t("BUCKET_PAGE_ALERT1")}
          message={t("BUCKET_PAGE_ALERT1_SUB")}
          link=""
          // linkText={`${t("BUCKET_PAGE_ALERT_LINK")}`}
        />
      </AlertsWrapper>
      <NavWrapper>
        <div>
          <Tabs
            tabs={visibleTabs}
            size='display'
            location={activeTab}
            onChange={setActiveTab}
          />
        </div>
      </NavWrapper>
      <Routes>
        <Route path="/" element={<Navigate replace to={defaultTab} />} /> 
        {canViewFiles && <Route path='/files' element={<FilesPage updateFunc={getData} />}/>} 
        {canViewSettings && <Route path='/settings' element={<SettingsPage bucket={bucket} />}/>} 
        {canViewLifecycleRules && <Route path='/lifecycle-rules' element={<LifecycleRulesPage />}/>} 
        <Route path='*' element={<Navigate replace to={defaultTab} />} />
      </Routes>
    </Container>
  )
}

export default BucketPage
